import React, { useMemo } from 'react'

import { find, map } from 'ramda'
import { useConnect } from 'redux-bundler-hook'

import { camelize } from 'inflection'

import { permitted, usePermissions } from '~/src/Flags/Can'
import { urls } from '~/src/Harvest/bundle'
import createLogger from '~/src/Lib/Logging'
import SplitButton from '~/src/UI/Shared/SplitButton'

import { getDateTime } from '../Lib/Utils'

const displayName = 'HG/PhaseButton'
const logger = createLogger(displayName)

const getNext = (harvestPhases, phase) => (phase ? find(p => p.sequence > phase.sequence, harvestPhases) : null)

const ACTIONS = {
  advance: {
    props: {
      label: ({ harvest }) => {
        const nextPhase = getNext(harvest.phases, harvest.currentPhase)
        return `Advance to ${nextPhase?.name}`
      },
      href: ({ harvest }) => `#/phaseAdvance/${harvest.id}`,
    },
    permissions: 'change_harvest',
  },
  delay: {
    props: {
      label: ({ harvest }) => {
        const lastConfirmed = harvest.phases.find(p => p.id === harvest.canDelayPhase)
        const firstUnconfirmed = getNext(harvest.phases, lastConfirmed)
        const isTheDayToChange = getDateTime('now').hasSame(getDateTime(firstUnconfirmed?.startDate), 'day')
        return `${isTheDayToChange ? 'Confirm or delay' : 'Delay'} start of ${firstUnconfirmed?.name ?? 'phase'}`
      },
      href: ({ harvest }) => `#/phaseDelay/${harvest.id}/${harvest.canDelayPhase}`,
    },
    permissions: 'change_harvest',
  },
  dry: {
    props: {
      label: 'End drying',
      href: ({ harvest }) => `#${urls.drying}/${harvest.id}`,
    },
    permissions: 'end_drying_flow',
  },
  harvest: {
    props: {
      label: 'Harvest plants',
      href: ({ harvest }) => `#${urls.harvesting}/${harvest.id}`,
    },
    permissions: 'harvest_flow',
  },
  transferPlants: {
    props: {
      label: 'Transfer to new Group',
      href: ({ harvest }) => urls.getUrl({ action: 'new' }, { sourceHarvest: harvest.id }),
    },
    permissions: 'lot2plant_flow',
  },
  transitionToNewGroup: {
    props: {
      label: 'Transfer to new Group',
      href: ({ harvest }) => urls.getUrl({ action: 'new' }, { sourceHarvest: harvest.id }),
    },
    permissions: 'lot2plant_flow',
  },
  lot2plant: {
    props: {
      label: 'Tag plants',
      href: ({ harvest }) => `#/lot2plant/${harvest.id}`,
    },
    permissions: 'lot2plant_flow',
  },
  veg2flower: {
    props: {
      label: 'Tag plants',
      href: ({ harvest }) => `#/veg2flower/${harvest.id}`,
    },
    permissions: 'lot2plant_flow',
  },
}

const getTransitionActions = props => {
  const { harvest, permissions } = props

  // in the order they should appear
  const actionKeys = [
    'lot2plant',
    'veg2flower',
    'harvest',
    'dry',
    'transferPlants',
    'transitionToNewGroup',
    'delay',
    'advance',
  ]
  const actions = []

  const callTheThingIfCallable = v => (typeof v === 'function' ? v(props) : v)

  actionKeys.forEach(key => {
    const can = harvest[`can${camelize(key)}`]
    const action = {
      ...ACTIONS[key],
      props: map(callTheThingIfCallable, ACTIONS[key].props),
    }

    if (can && permitted(permissions, action.permissions, true)) {
      actions.push({
        ...action.props,
        key,
        variant: can === 'SHOULD' ? 'contained' : 'text',
      })
    }
  })

  return actions
}

export const HGPhaseButton = props => {
  const { className, harvest, phases } = props

  const permissions = usePermissions()

  const harvestPhases = harvest.phases
    .map(p => (typeof p !== 'object' ? phases[p] : p))
    .sort((a, b) => a.sequence - b.sequence)
    .filter(Boolean)

  const fullHarvest = {
    ...harvest,
    phases: harvestPhases,
    currentPhase: typeof harvest.currentPhase !== 'object' ? phases[harvest.currentPhase] : harvest.currentPhase
  }

  const kwargs = useMemo(() => ({ ...props, permissions, harvest: fullHarvest }), [props, permissions])
  const actions = getTransitionActions(kwargs)

  if (!actions.length) return <span />

  return <SplitButton options={actions} className={className} data-testid="harvest-phase-action" />
}
HGPhaseButton.displayName = displayName

export default React.memo(props => {
  const { phases } = useConnect('selectPhases')

  return <HGPhaseButton phases={phases} {...props} />
})
